/**
 * Pool Reports — "Online account history" (30/7). A daily snapshot of the whole Online Channel estate
 * (the nightly 23:00 job, `snapshot.daily` in the audit log — see poolAuditFormat.ts), so an operator
 * can see the estate's Seats/Assigned/Unassigned/Utilization picture on any PAST day, not just today's
 * live GET /pool/accounts (which is what the Dashboard's own Overview reads). `_manage`-gated exactly
 * like the Dashboard — the gate ladder below is copied from PoolDashboardPage.tsx's own template
 * verbatim: PoolLayout's tab visibility is NOT a security boundary, this screen asserts its own gate
 * regardless of how it was reached.
 *
 * Two views, driven by whether `?date=` is set (bookmarkable, same convention as PoolUsersPage's own
 * `?email=`/`?account=`):
 *
 *  - LIST (no `?date=`) — a DateRangePicker (default one calendar month back from today, max = today)
 *    drives usePoolReportsDaily directly: unlike Customer Reports' explicit Generate button, this is a
 *    cheap indexed read over a small per-day table, so it auto-loads on the default range and on every
 *    range change — no Generate gate to copy. Two charts on ONE shared day axis (principal 31/7:
 *    daily movement over %util) — assigned-licences daily change (DivergingBarChart, deltas computed
 *    client-side in poolReportTrend.ts) above the absolute assigned level per day (the shared
 *    `BarChart`) — sit above a DESC table of days; a date-cell link navigates into the day view.
 *  - DAY (`?date=YYYY-MM-DD` set) — that day's own numbers: a "By account type" breakdown (the EXACT
 *    same aggregatePoolStats/bucketByTier/tierLabel formulas the Dashboard's Overview uses — never a
 *    forked copy, see poolAccountStats.ts) plus a per-account table matching Pool Accounts' own column
 *    conventions. A 404 (no snapshot for that date — before the module existed, or a night the sweep
 *    skipped) renders a dedicated "no snapshot" state, not a generic error.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ApiRequestError } from "@/api/client";
import {
  DateRangePicker,
  EmptyState,
  ErrorState,
  SkeletonRows,
  Table,
  type Column,
  type DateRange,
} from "@/components/ui";
import { BarChart, DivergingBarChart } from "@/features/customer/charts";
import { Section } from "@/features/customer/display";
import { assignedDeltaSeries } from "@/features/pool/poolReportTrend";
import { PoolErrorState } from "@/features/pool/PoolErrorState";
import {
  aggregatePoolStats,
  bucketByTier,
  tierLabel,
  unassignedCount,
  utilizationPct,
} from "@/features/pool/poolAccountStats";
import { usePoolAccess, usePoolReportDay, usePoolReportsDaily } from "@/features/pool/hooks";
import { formatDate, formatInstant, formatInt } from "@/lib/format";
import { tierOf } from "@/lib/planNames";
import type { PoolReportAccountRow, PoolReportDay } from "@/api/types";

const todayIsoBkk = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Bangkok" }).format(new Date());

/** "One calendar month back" (principal 31/7: default period = วันนี้ − 1 เดือน, deliberately NOT a
 *  fixed 30-day window — mid-month the two differ by weeks): same day-of-month in the previous month,
 *  clamped to that month's last day (31 Jul → 30 Jun, 31 Mar → 28/29 Feb). UTC math on the resolved
 *  "YYYY-MM-DD" string, same no-host-timezone idiom as the rest of this file's date arithmetic. */
export function isoMinusOneMonth(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  // Date.UTC(y, m-1, 0) = day 0 of the CURRENT month index = the previous month's last day.
  const lastDayPrevMonth = new Date(Date.UTC(y, m - 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, m - 2, Math.min(d, lastDayPrevMonth))).toISOString().slice(0, 10);
}

/** Short "D/M" axis label for the trend chart — mirrors ReportsTab's own daily trendLabel() rather
 *  than the long formatDate() the list table uses (a 30-bar chart needs a narrow label). */
function shortDayLabel(dateIso: string): string {
  const [, m, d] = dateIso.split("-");
  return m && d ? `${Number(d)}/${Number(m)}` : dateIso;
}

/**
 * 30/7 — `_manage` only (owner/admin), copied verbatim from PoolDashboardPage's own gate: a pool-Viewer
 * (or a non-operator) gets the app's normal RBAC-forbidden copy, never <PoolErrorState/> — that copy
 * means "not a pool operator", a different story from "you're an operator, just not manage-capable".
 */
export function PoolReportsPage() {
  const { t } = useTranslation();
  const accessQ = usePoolAccess();

  if (accessQ.isPending) return <SkeletonRows rows={3} />;
  if (accessQ.isError) {
    return <ErrorState error={accessQ.error} onRetry={() => void accessQ.refetch()} />;
  }

  const role = accessQ.data.role;
  if (role !== "owner" && role !== "admin") {
    return <ErrorState title={t("state.forbiddenTitle")} body={t("state.forbiddenBody")} />;
  }

  return <PoolReportsContent />;
}

function PoolReportsContent() {
  const [params] = useSearchParams();
  const date = params.get("date");

  return date ? <PoolReportDayView date={date} /> : <PoolReportsListView />;
}

// --- List view: DateRangePicker + trend chart + a DESC table of days -------------------------------

function PoolReportsListView() {
  const { t } = useTranslation();
  const todayStr = todayIsoBkk();
  const [range, setRange] = usePoolReportsRange(todayStr);
  const reportsQ = usePoolReportsDaily(range);

  const columns: Column<PoolReportDay>[] = [
    {
      key: "date",
      header: t("pool.reports.colDate"),
      sortValue: (r) => r.snapshot_date,
      // The ONLY navigation element into the day view (?date=…) — a real <Link>, same convention as
      // Pool Accounts' own Account Name cell (rev 9 there): DESIGN.md's Table signature explicitly
      // rules out a whole row silently acting as a button, so this is a declared, keyboard-focusable
      // control rather than an onRowClick on the <tr>.
      cell: (r) => (
        <Link
          to={`/pool/reports?date=${encodeURIComponent(r.snapshot_date)}`}
          className="font-medium text-(--brand-strong) hover:underline"
        >
          {formatDate(r.snapshot_date)}
        </Link>
      ),
    },
    {
      key: "accounts",
      header: t("pool.reports.colAccounts"),
      numeric: true,
      sortValue: (r) => r.accounts,
      cell: (r) => formatInt(r.accounts),
    },
    {
      key: "seats",
      header: t("pool.reports.colSeats"),
      numeric: true,
      sortValue: (r) => r.seats ?? -1,
      cell: (r) => (r.seats != null ? formatInt(r.seats) : "—"),
    },
    {
      key: "assigned",
      header: t("pool.reports.colAssigned"),
      numeric: true,
      sortValue: (r) => r.assigned,
      cell: (r) => formatInt(r.assigned),
    },
    {
      key: "unassigned",
      header: t("pool.reports.colUnassigned"),
      numeric: true,
      sortValue: (r) => r.unassigned,
      cell: (r) =>
        r.unassigned == null ? (
          "—"
        ) : (
          <span className={r.unassigned < 0 ? "font-semibold text-(--warn)" : undefined}>
            {formatInt(r.unassigned)}
          </span>
        ),
    },
    {
      key: "utilization",
      header: t("pool.reports.colUtilization"),
      numeric: true,
      sortValue: (r) => r.utilization_pct ?? -1,
      cell: (r) =>
        r.utilization_pct == null ? (
          "—"
        ) : (
          <span className={r.utilization_pct > 100 ? "font-semibold text-(--warn)" : undefined}>
            {`${r.utilization_pct}%`}
          </span>
        ),
    },
  ];

  const days = reportsQ.data?.days ?? [];
  // Server returns `days` DESC (newest first, for the table) — the charts read chronologically.
  const chronological = [...days].reverse();
  const deltaPoints = assignedDeltaSeries(chronological);

  return (
    <section aria-labelledby="pool-reports-title" className="flex flex-col gap-6">
      <header className="flex flex-col gap-1.5">
        <h1
          id="pool-reports-title"
          className="text-(length:--text-2xl) font-semibold text-(--text-strong)"
        >
          {t("pool.reports.title")}
        </h1>
        <p className="text-(length:--text-sm) text-(--text-muted)">{t("pool.reports.subtitle")}</p>
      </header>

      <div className="flex flex-col gap-1">
        <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
          {t("pool.reports.period")}
        </span>
        <DateRangePicker
          value={range}
          onChange={setRange}
          max={todayStr}
          aria-label={t("pool.reports.period")}
          className="w-fit"
        />
      </div>

      {reportsQ.isPending ? (
        <SkeletonRows rows={5} />
      ) : reportsQ.isError ? (
        <PoolErrorState error={reportsQ.error} onRetry={() => void reportsQ.refetch()} />
      ) : days.length === 0 ? (
        <EmptyState title={t("pool.reports.emptyTitle")} body={t("pool.reports.emptyBody")} />
      ) : (
        <>
          {/* Two stacked charts on ONE shared day axis (same labels, same order, same column cap) so
              the eye can drop vertically from a spike in the delta chart to the level it produced.
              Delta leads in brand indigo (it IS the question this screen answers — principal 31/7:
              "อยากเห็นการเปลี่ยนแปลงของ License ที่ใช้งาน มากกว่าการดู %Utilization"); the level chart
              reads in the neutral data tone as supporting context — same metric, quieter voice, and
              two full-height indigo fields would blow The One Accent Rule's ≤10% budget. */}
          <Section title={t("pool.reports.deltaTitle")}>
            <DivergingBarChart
              bars={deltaPoints.map((p) => ({
                label: shortDayLabel(p.snapshot_date),
                value: p.delta,
              }))}
              caption={t("pool.reports.deltaTitle")}
              unit={t("pool.reports.deltaUnit")}
              tone="brand"
            />
            {deltaPoints.some((p) => p.delta == null) && (
              <p className="text-(length:--text-xs) text-(--text-subtle)">
                {t("pool.reports.deltaNoBaseline")}
              </p>
            )}
          </Section>

          <Section title={t("pool.reports.levelTitle")}>
            <BarChart
              bars={chronological.map((d) => ({
                label: shortDayLabel(d.snapshot_date),
                value: d.assigned,
              }))}
              caption={t("pool.reports.levelTitle")}
              unit={t("pool.reports.levelUnit")}
              tone="neutral"
            />
          </Section>

          <Table
            columns={columns}
            rows={days}
            rowKey={(r) => r.snapshot_date}
            caption={t("pool.reports.title")}
            initialSort={{ key: "date", dir: "desc" }}
            paginated={false}
          />
        </>
      )}
    </section>
  );
}

/** Default the LIST view's range to one calendar month back (max = today) — a `useState` seeded once
 *  from the Bangkok "today" so the default doesn't drift as the component re-renders. Pulled into its
 *  own tiny hook only to keep PoolReportsListView's own body free of the seeding boilerplate. */
function usePoolReportsRange(todayStr: string): [DateRange, (r: DateRange) => void] {
  const [range, setRange] = useState<DateRange>(() => ({
    start: isoMinusOneMonth(todayStr),
    end: todayStr,
  }));
  return [range, setRange];
}

// --- Day view: header + "By account type" breakdown (Dashboard's own formulas) + per-account table ---

function PoolReportDayView({ date }: { date: string }) {
  const { t } = useTranslation();
  const dayQ = usePoolReportDay(date, true);

  const notFound =
    dayQ.isError && dayQ.error instanceof ApiRequestError && dayQ.error.error.status === 404;

  return (
    <section aria-labelledby="pool-report-day-title" className="flex flex-col gap-6">
      <div className="flex flex-col gap-1.5">
        <Link
          to="/pool/reports"
          className="inline-flex w-fit items-center gap-1 text-(length:--text-sm) font-medium text-(--brand-strong) hover:underline"
        >
          <ArrowLeft className="size-4" aria-hidden />
          {t("pool.reports.backToList")}
        </Link>
        <h1
          id="pool-report-day-title"
          className="text-(length:--text-2xl) font-semibold text-(--text-strong)"
        >
          {formatDate(date)}
        </h1>
        {dayQ.data && (
          <p className="text-(length:--text-sm) text-(--text-muted)">
            {t("pool.reports.capturedAt", { when: formatInstant(dayQ.data.captured_at) })}
          </p>
        )}
      </div>

      {dayQ.isPending ? (
        <SkeletonRows rows={5} />
      ) : notFound ? (
        <EmptyState
          title={t("pool.reports.dayNotFoundTitle")}
          body={t("pool.reports.dayNotFoundBody")}
        />
      ) : dayQ.isError ? (
        <PoolErrorState error={dayQ.error} onRetry={() => void dayQ.refetch()} />
      ) : (
        <PoolReportDayBody accounts={dayQ.data.accounts} />
      )}
    </section>
  );
}

interface DayTierRow {
  tier: string;
  accounts: number;
  seats: number | null;
  assigned: number;
  unassigned: number | null;
  utilizationPct: number | null;
}

function PoolReportDayBody({ accounts }: { accounts: PoolReportAccountRow[] }) {
  const { t } = useTranslation();

  const buckets = bucketByTier(accounts);
  const tierRows: DayTierRow[] = buckets.map(({ tier, rows }) => ({
    tier,
    ...aggregatePoolStats(rows),
  }));

  const tierColumns: Column<DayTierRow>[] = [
    {
      key: "tier",
      header: t("pool.dashboard.overview.colAccountType"),
      cell: (r) => tierLabel(t, r.tier),
    },
    {
      key: "accounts",
      header: t("pool.dashboard.overview.colAccounts"),
      numeric: true,
      cell: (r) => formatInt(r.accounts),
    },
    {
      key: "seats",
      header: t("pool.dashboard.overview.colSeats"),
      numeric: true,
      cell: (r) => (r.seats != null ? formatInt(r.seats) : "—"),
    },
    {
      key: "assigned",
      header: t("pool.dashboard.overview.colAssigned"),
      numeric: true,
      cell: (r) => formatInt(r.assigned),
    },
    {
      key: "unassigned",
      header: t("pool.dashboard.overview.colUnassigned"),
      numeric: true,
      cell: (r) =>
        r.unassigned == null ? (
          "—"
        ) : (
          <span className={r.unassigned < 0 ? "font-semibold text-(--warn)" : undefined}>
            {formatInt(r.unassigned)}
          </span>
        ),
    },
    {
      key: "utilization",
      header: t("pool.dashboard.overview.colUtilization"),
      numeric: true,
      cell: (r) =>
        r.utilizationPct == null ? (
          "—"
        ) : (
          <span className={r.utilizationPct > 100 ? "font-semibold text-(--warn)" : undefined}>
            {`${r.utilizationPct}%`}
          </span>
        ),
    },
  ];

  const accountColumns: Column<PoolReportAccountRow>[] = [
    {
      key: "name",
      header: t("pool.reports.colAccount"),
      sortValue: (r) => r.account_name ?? "",
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <span className="font-medium text-(--text)">{r.account_name ?? "—"}</span>
          <span className="text-(length:--text-2xs) text-(--text-muted)">
            {r.account_number ?? "—"}
          </span>
        </span>
      ),
    },
    {
      key: "type",
      header: t("pool.reports.colType"),
      sortValue: (r) => tierOf(r.account_type) ?? "",
      cell: (r) => tierOf(r.account_type) ?? "—",
    },
    {
      key: "seats",
      header: t("pool.dashboard.overview.colSeats"),
      numeric: true,
      sortValue: (r) => r.hosts_total ?? -1,
      cell: (r) => (r.hosts_total != null ? formatInt(r.hosts_total) : "—"),
    },
    {
      key: "assigned",
      header: t("pool.dashboard.overview.colAssigned"),
      numeric: true,
      sortValue: (r) => r.licensed_members,
      cell: (r) => formatInt(r.licensed_members),
    },
    {
      key: "unassigned",
      header: t("pool.dashboard.overview.colUnassigned"),
      numeric: true,
      sortValue: (r) => unassignedCount(r),
      cell: (r) => {
        const n = unassignedCount(r);
        if (n == null) return "—";
        return (
          <span className={n < 0 ? "font-semibold text-(--warn)" : undefined}>{formatInt(n)}</span>
        );
      },
    },
    {
      key: "utilization",
      header: t("pool.dashboard.overview.colUtilization"),
      numeric: true,
      sortValue: (r) => utilizationPct(r) ?? -1,
      cell: (r) => {
        const pct = utilizationPct(r);
        if (pct == null) return "—";
        return (
          <span className={pct > 100 ? "font-semibold text-(--warn)" : undefined}>{`${pct}%`}</span>
        );
      },
    },
  ];

  return (
    <>
      <Section title={t("pool.dashboard.overview.breakdownTitle")}>
        <Table
          columns={tierColumns}
          rows={tierRows}
          rowKey={(r) => r.tier}
          caption={t("pool.dashboard.overview.breakdownTitle")}
          paginated={false}
        />
      </Section>

      <Section title={t("pool.reports.accountsTitle")}>
        <Table
          columns={accountColumns}
          rows={accounts}
          rowKey={(r) => r.account_id}
          caption={t("pool.reports.accountsTitle")}
          paginated={false}
        />
      </Section>
    </>
  );
}
